// Central product catalog for Sango Terrazzo.
// Used by Production / Dispatch / Schedule forms for cascading dropdowns.

export const COMMON_COLORS = ["grey", "red", "yellow", "chocolate", "black"];

// Terrazzo standard + signature colors (used for terrazzo tiles, countertops, wash basins)
export const TERRAZZO_COLORS = [
  { key: "onyx_classic", label: "Onyx Classic", collection: "standard" },
  { key: "snowfall", label: "Snowfall", collection: "standard" },
  { key: "salt_and_pepper", label: "Salt & Pepper", collection: "standard" },
  { key: "ash", label: "Ash", collection: "standard" },
  { key: "charcoal_frost", label: "Charcoal Frost", collection: "standard" },
  { key: "ivory_pebble", label: "Ivory Pebble", collection: "standard" },
  { key: "bone_and_bark", label: "Bone & Bark", collection: "standard" },
  { key: "terra_rossa", label: "Terra Rossa", collection: "standard" },
  { key: "brick_lane", label: "Brick Lane", collection: "standard" },
  { key: "ruby_ash", label: "Ruby Ash", collection: "standard" },
  { key: "rosewood", label: "Rosewood", collection: "standard" },
  { key: "cocoa_cream", label: "Cocoa Cream", collection: "standard" },
  { key: "blush_stone", label: "Blush Stone", collection: "standard" },
  { key: "meadow", label: "Meadow", collection: "standard" },
  { key: "olive_grove", label: "Olive Grove", collection: "standard" },
  { key: "deep_forest", label: "Deep Forest", collection: "standard" },
  { key: "emerald_glass", label: "Emerald Glass", collection: "standard" },
  { key: "fern_light", label: "Fern Light", collection: "standard" },
  { key: "red_cedar", label: "Red Cedar", collection: "standard" },
  { key: "scarlet_noir", label: "Scarlet Noir", collection: "standard" },
  { key: "verde_nero", label: "Verde Nero", collection: "standard" },
  { key: "recycled_green", label: "Recycled Green", collection: "standard" },
  { key: "sea_glass", label: "Sea Glass", collection: "standard" },
  { key: "amber_woods", label: "Amber Woods", collection: "standard" },
  { key: "pepper_field", label: "Pepper Field", collection: "standard" },
  // Signature collection (15)
  { key: "celestial_white", label: "Celestial White", collection: "signature" },
  { key: "midnight_quartz", label: "Midnight Quartz", collection: "signature" },
  { key: "rose_marble", label: "Rose Marble", collection: "signature" },
  { key: "champagne_toast", label: "Champagne Toast", collection: "signature" },
  { key: "smoked_pearl", label: "Smoked Pearl", collection: "signature" },
  { key: "amber_ember", label: "Amber Ember", collection: "signature" },
  { key: "forest_jewel", label: "Forest Jewel", collection: "signature" },
  { key: "onyx_rose", label: "Onyx Rose", collection: "signature" },
  { key: "golden_sand", label: "Golden Sand", collection: "signature" },
  { key: "storm_grey", label: "Storm Grey", collection: "signature" },
  { key: "ruby_noir", label: "Ruby Noir", collection: "signature" },
  { key: "sage_luxe", label: "Sage Luxe", collection: "signature" },
  { key: "cocoa_gold", label: "Cocoa Gold", collection: "signature" },
  { key: "pink_salt", label: "Pink Salt", collection: "signature" },
  { key: "emerald_onyx", label: "Emerald Onyx", collection: "signature" },
];

export const CATEGORIES = [
  {
    key: "pavers",
    label: "Interlocking Pavers",
    variants: [
      { key: "zebra", label: "Zebra" },
      { key: "ishape", label: "I-Shape" },
      { key: "hexa", label: "Hexa" },
      { key: "v", label: "V-Shape" },
      { key: "4x8", label: "4×8" },
      { key: "kerb_stone", label: "Kerb Stone" },
      { key: "saucer_drain", label: "Saucer Drain" },
    ],
    colors: COMMON_COLORS.map((c) => ({ key: c, label: c[0].toUpperCase() + c.slice(1) })),
    sizes: [],
    defaultUnit: "pcs",
  },
  {
    key: "chequered_tiles",
    label: "Chequered Tiles",
    variants: [
      { key: "zigzag", label: "Zigzag" },
      { key: "20_dibbi", label: "20 Dibbi" },
      { key: "100_dibbi", label: "100 Dibbi" },
      { key: "flower", label: "Flower" },
    ],
    colors: COMMON_COLORS.map((c) => ({ key: c, label: c[0].toUpperCase() + c.slice(1) })),
    sizes: [],
    defaultUnit: "pcs",
  },
  {
    key: "terrazzo_tiles",
    label: "Terrazzo Tiles",
    variants: [
      { key: "wall", label: "Wall Tile" },
      { key: "floor", label: "Floor Tile" },
    ],
    colors: TERRAZZO_COLORS,
    sizes: [
      { key: "1x1", label: "1×1" },
      { key: "4x2", label: "4×2" },
      { key: "4x6", label: "4×6" },
      { key: "4x8", label: "4×8" },
    ],
    defaultUnit: "pcs",
  },
  {
    key: "countertops",
    label: "Kitchen Countertops",
    variants: [{ key: "25mm", label: "25mm thickness" }],
    colors: TERRAZZO_COLORS,
    sizes: [], // custom sizes — captured in product_name / notes
    defaultUnit: "sqft",
    customSize: true,
  },
  {
    key: "wash_basins",
    label: "Wash Basins",
    variants: [
      { key: "square", label: "Square" },
      { key: "panache", label: "Panache" },
      { key: "ultimo", label: "Ultimo" },
      { key: "luxe", label: "Luxe" },
      { key: "pantheon", label: "Pantheon" },
    ],
    colors: TERRAZZO_COLORS,
    sizes: [],
    defaultUnit: "pcs",
  },
];

export const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

// Build a display product_name from selections
export function buildProductName({ category, variant, color, size, custom_size }) {
  const cat = CATEGORY_MAP[category];
  if (!cat) return "";
  const parts = [];
  const v = cat.variants.find((x) => x.key === variant);
  if (v) parts.push(v.label);
  const c = cat.colors.find((x) => x.key === color);
  if (c) parts.push(c.label);
  const s = cat.sizes.find((x) => x.key === size);
  if (s) parts.push(s.label);
  if (cat.customSize && custom_size) parts.push(custom_size);
  return parts.join(" · ");
}

export const prettyCat = (k) => CATEGORY_MAP[k]?.label || (k || "").replace(/_/g, " ");
